export type VideoSource = {
  src: string
  type?: string
}

const MP4_PATHS_WITH_WEBM_COMPANION = new Set<string>(['/HomePageVideo.mp4'])
const POSTER_PATH_BY_VIDEO_PATH = new Map<string, string>([
  ['/HomePageVideo.mp4', '/HomePageVideo.poster.jpg'],
  ['/HomePageVideo.webm', '/HomePageVideo.poster.jpg'],
])

function getExtension(pathname: string): string {
  const dotIndex = pathname.lastIndexOf('.')
  if (dotIndex < 0) return ''
  return pathname.slice(dotIndex).toLowerCase()
}

function toWebmPath(pathWithOptionalQuery: string): string {
  const match = pathWithOptionalQuery.match(/^([^?#]+)([?#].*)?$/)
  const pathname = match?.[1] ?? pathWithOptionalQuery
  const suffix = match?.[2] ?? ''
  return pathname.replace(/\.mp4$/i, '.webm') + suffix
}

function mediaTypeForExtension(extension: string): string | undefined {
  if (extension === '.webm') return 'video/webm'
  if (extension === '.mp4') return 'video/mp4'
  return undefined
}

function getPathnameAndSuffix(pathWithOptionalQuery: string): { pathname: string; suffix: string } {
  const match = pathWithOptionalQuery.match(/^([^?#]+)([?#].*)?$/)
  return {
    pathname: match?.[1] ?? pathWithOptionalQuery,
    suffix: match?.[2] ?? '',
  }
}

export function getPreferredVideoSources(videoSrc: string | null | undefined): VideoSource[] {
  const normalizedSrc = videoSrc?.trim()
  if (!normalizedSrc) return []

  const { pathname } = getPathnameAndSuffix(normalizedSrc)
  const extension = getExtension(pathname)
  const isLocalMp4 = pathname.startsWith('/') && extension === '.mp4'
  const hasKnownWebmCompanion = MP4_PATHS_WITH_WEBM_COMPANION.has(pathname)

  if (isLocalMp4 && hasKnownWebmCompanion) {
    return [
      { src: toWebmPath(normalizedSrc), type: 'video/webm' },
      { src: normalizedSrc, type: 'video/mp4' },
    ]
  }

  return [{ src: normalizedSrc, type: mediaTypeForExtension(extension) }]
}

export function getVideoPosterSrc(videoSrc: string | null | undefined): string | undefined {
  const normalizedSrc = videoSrc?.trim()
  if (!normalizedSrc) return undefined

  const { pathname, suffix } = getPathnameAndSuffix(normalizedSrc)
  if (!pathname.startsWith('/')) return undefined

  const posterPath = POSTER_PATH_BY_VIDEO_PATH.get(pathname)
  if (!posterPath) return undefined

  return `${posterPath}${suffix}`
}
